import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Crown, Plus, Save, Trash2, Edit3, Eye, EyeOff, ChevronUp, ChevronDown, X, Upload, Image as ImageIcon } from "lucide-react";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { renderMarkdownSafe } from "../../lib/safeMarkdown";
import { extractApiError } from "../../lib/apiError";

export default function LuxuryOptionsTab() {
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [reordering, setReordering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, vres] = await Promise.all([
        axios.get(`${API}/admin/luxury-options`),
        axios.get(`${API}/admin/vehicles`),
      ]);
      setItems(res.data || []);
      setVehicles(vres.data || []);
    } catch (e) { toast.error(extractApiError(e, "Failed to load")); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (payload, id) => {
    try {
      if (id) await axios.put(`${API}/admin/luxury-options/${id}`, payload);
      else await axios.post(`${API}/admin/luxury-options`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(extractApiError(e, "Failed to save")); }
  };

  const remove = async (opt) => {
    if (!window.confirm(`Delete "${opt.title}"?`)) return;
    try { await axios.delete(`${API}/admin/luxury-options/${opt.luxury_option_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(extractApiError(e, "Failed to delete")); }
  };

  const togglePublished = async (opt) => {
    try { await axios.put(`${API}/admin/luxury-options/${opt.luxury_option_id}`, { published: !opt.published }); load(); }
    catch (e) { toast.error(extractApiError(e, "Failed")); }
  };

  const move = async (index, dir) => {
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    const list = [...items];
    const [m] = list.splice(index, 1);
    list.splice(newIdx, 0, m);
    setItems(list); setReordering(true);
    try { await axios.post(`${API}/admin/luxury-options/reorder`, { ordered_ids: list.map(i => i.luxury_option_id) }); }
    catch (e) { toast.error(extractApiError(e, "Reorder failed")); load(); }
    finally { setReordering(false); }
  };

  return (
    <div data-testid="luxury-options-tab" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Crown className="w-5 h-5 text-[#C5A059]" /> Premium Enhancements
          </h2>
          <p className="text-white/40 text-xs mt-1">
            Multi-select premium enhancements shown in the public booking form. Each option adds a % surcharge to the route price.
          </p>
        </div>
        <Button onClick={() => setEditing("new")} className="btn-gold" data-testid="luxury-add-btn"><Plus className="w-4 h-4 mr-2" /> Add option</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="spinner" /></div>
      ) : items.length === 0 ? (
        <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-8 text-center text-white/50">No options yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((opt, idx) => (
            <div key={opt.luxury_option_id} className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4 flex items-start gap-3" data-testid={`luxury-row-${opt.luxury_option_id}`}>
              <div className="flex flex-col gap-1">
                <button onClick={() => move(idx, -1)} disabled={reordering || idx === 0} className="text-white/30 hover:text-[#C5A059] disabled:opacity-20"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => move(idx, 1)} disabled={reordering || idx === items.length - 1} className="text-white/30 hover:text-[#C5A059] disabled:opacity-20"><ChevronDown className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] text-white/30 font-mono">#{idx + 1}</span>
                  <span className="text-[10px] bg-[#C5A059]/15 text-[#C5A059] px-2 py-0.5 rounded-sm font-semibold">+{opt.surcharge_pct}%</span>
                  {!opt.published && <span className="text-[10px] uppercase text-orange-400 bg-orange-400/10 px-2 py-0.5 rounded-sm">Hidden</span>}
                </div>
                <p className="text-white font-medium truncate">{opt.title}</p>
                <p className="text-white/50 text-xs">{opt.short_description}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => togglePublished(opt)} className="text-white/60 hover:text-[#C5A059]" data-testid={`luxury-toggle-${opt.luxury_option_id}`}>{opt.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</Button>
                <Button variant="ghost" size="sm" onClick={() => setEditing(opt)} className="text-white/60 hover:text-[#C5A059]" data-testid={`luxury-edit-${opt.luxury_option_id}`}><Edit3 className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => remove(opt)} className="text-white/60 hover:text-red-400" data-testid={`luxury-delete-${opt.luxury_option_id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <LuxuryEditor initial={editing === "new" ? null : editing} vehicles={vehicles} onClose={() => setEditing(null)} onSave={(p) => save(p, editing === "new" ? null : editing.luxury_option_id)} />
      )}
    </div>
  );
}

function LuxuryEditor({ initial, onClose, onSave, vehicles = [] }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [shortDesc, setShortDesc] = useState(initial?.short_description || "");
  const [fullDetails, setFullDetails] = useState(initial?.full_details || "");
  const [surcharge, setSurcharge] = useState(initial?.surcharge_pct?.toString() ?? "5");
  const [imageUrl, setImageUrl] = useState(initial?.image_url || "");
  const [uploadingImg, setUploadingImg] = useState(false);
  const [published, setPublished] = useState(initial ? !!initial.published : true);
  const [autoAssignVehicleId, setAutoAssignVehicleId] = useState(initial?.auto_assign_vehicle_id || "");
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const previewSrc = imageUrl
    ? (imageUrl.startsWith("/api/") ? `${BACKEND_URL}${imageUrl}` : imageUrl)
    : null;

  const previewHtml = useMemo(() => ({ __html: renderMarkdownSafe(fullDetails, "luxury-preview") }), [fullDetails]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large (max 5 MB)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploadingImg(true);
    try {
      const res = await axios.post(`${API}/admin/uploads/image`, fd, { headers: { "Content-Type": "multipart/form-data" },
      });
      setImageUrl(res.data.url);
      toast.success("Image uploaded — click Save to apply");
    } catch (err) {
      toast.error(extractApiError(err, "Upload failed"));
    } finally { setUploadingImg(false); }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!title.trim()) { toast.error("Title is required"); return; }
    const pct = parseFloat(surcharge);
    if (Number.isNaN(pct) || pct < 0) { toast.error("Surcharge must be a non-negative number"); return; }
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        short_description: shortDesc.trim(),
        full_details: fullDetails.trim(),
        surcharge_pct: pct,
        image_url: imageUrl,
        published,
        auto_assign_vehicle_id: autoAssignVehicleId || "",
      });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="luxury-editor-modal">
      <form onSubmit={submit} className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h3 className="text-lg font-semibold text-white">{initial ? "Edit option" : "New luxury option"}</h3>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-white/50 text-[10px] uppercase mb-1 block">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="input-dark" required maxLength={120} data-testid="luxury-editor-title" />
          </div>
          <div>
            <label className="text-white/50 text-[10px] uppercase mb-1 block">Short description (shown as 2nd line)</label>
            <Input value={shortDesc} onChange={(e) => setShortDesc(e.target.value)} className="input-dark" maxLength={200} data-testid="luxury-editor-short" />
          </div>
          <div>
            <label className="text-white/50 text-[10px] uppercase mb-1 block">Surcharge (%)</label>
            <Input type="number" min="0" step="0.5" value={surcharge} onChange={(e) => setSurcharge(e.target.value)} className="input-dark w-32 font-mono" data-testid="luxury-editor-surcharge" />
          </div>
          {/* Image upload (shown in the public details popup) */}
          <div>
            <label className="text-white/50 text-[10px] uppercase mb-2 block flex items-center gap-2">
              <ImageIcon className="w-3.5 h-3.5 text-[#C5A059]" /> Cover image (shown in customer details popup)
            </label>
            <div className="flex items-start gap-4">
              <div className="w-24 h-24 rounded-sm border border-white/10 bg-[#0A0A0A] flex items-center justify-center overflow-hidden flex-shrink-0" data-testid="luxury-image-preview">
                {previewSrc ? (
                  <img src={previewSrc} alt="Cover" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <ImageIcon className="w-6 h-6 text-white/20" />
                )}
              </div>
              <div className="flex-1 flex flex-col gap-2">
                <label
                  className={`btn-gold inline-flex items-center gap-2 px-4 py-2 text-xs cursor-pointer rounded-sm w-fit ${uploadingImg ? "opacity-60 pointer-events-none" : ""}`}
                  data-testid="luxury-image-upload-btn"
                >
                  <Upload className="w-3.5 h-3.5" />
                  {uploadingImg ? "Uploading..." : "Upload image"}
                  <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleImageUpload} className="hidden" disabled={uploadingImg} />
                </label>
                {imageUrl && (
                  <button type="button" onClick={() => setImageUrl("")} className="text-red-400/80 hover:text-red-400 text-xs flex items-center gap-1 w-fit" data-testid="luxury-image-remove-btn">
                    <X className="w-3 h-3" /> Remove image
                  </button>
                )}
                <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="/api/files/... or https://..." className="input-dark font-mono text-xs" data-testid="luxury-image-url-input" />
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-white/50 text-[10px] uppercase">Full details (Markdown) — shown in the details popup</label>
              <button type="button" onClick={() => setPreview(p => !p)} className="text-[#C5A059] text-xs hover:underline">{preview ? "Edit" : "Preview"}</button>
            </div>
            {preview ? (
              <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-4 min-h-[180px] text-white/80 prose-custom prose-sm" dangerouslySetInnerHTML={previewHtml} />
            ) : (
              <textarea value={fullDetails} onChange={(e) => setFullDetails(e.target.value)} className="w-full h-48 bg-[#0A0A0A] border border-[#333] rounded-sm p-4 text-white/80 text-sm font-mono resize-y focus:border-[#C5A059]/50 focus:outline-none" maxLength={20000} spellCheck="false" data-testid="luxury-editor-details" />
            )}
          </div>
          <div>
            <label className="text-white/50 text-[10px] uppercase mb-1 block">Auto-assign vehicle (optional)</label>
            <select
              value={autoAssignVehicleId}
              onChange={(e) => setAutoAssignVehicleId(e.target.value)}
              className="input-dark text-sm w-full"
              data-testid="luxury-editor-auto-vehicle"
            >
              <option value="">— No auto-assign —</option>
              {vehicles.map((v) => (
                <option key={v.vehicle_id} value={v.vehicle_id}>
                  {v.title} ({v.max_passengers} pax)
                </option>
              ))}
            </select>
            <p className="text-white/30 text-[10px] mt-1">
              When set (e.g. "Luxury V Limo"), selecting this option auto-assigns the chosen vehicle to the booking. The option is hidden in the public form when the passenger count exceeds the vehicle's seats.
            </p>
          </div>
          <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
            <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} className="accent-[#C5A059]" data-testid="luxury-editor-published" /> Published
          </label>
        </div>
        <div className="p-5 border-t border-white/5 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} className="text-white/60">Cancel</Button>
          <Button type="submit" disabled={saving} className="btn-gold" data-testid="luxury-editor-save"><Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save"}</Button>
        </div>
      </form>
    </div>
  );
}
